import React, { useEffect, useState } from 'react';
import api from '../api/api';
import Swal from 'sweetalert2';
import { X, Printer, Clock, Car, User, MapPin, Gauge, Calendar, DollarSign, AlertCircle } from 'lucide-react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

export default function StartReservationModal({ reservation, onClose, onSuccess }) {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const res = await api.get(`/reservations/details/${reservation.id}`);
                setDetails(res.data);
            } catch (error) {
                console.error(t('error_loading_details'), error);
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('error_details'),
                    confirmButtonColor: '#3b82f6'
                });
                onClose();
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [reservation.id, onClose, t]);

    const isToday = (dateStr) => {
        return moment(dateStr).isSame(moment(), 'day');
    };

    const handleStart = async () => {
        try {
            await api.post(`/reservations/start/${reservation.id}`);
            Swal.fire({
                icon: 'success',
                title: t('reservation_started'),
                text: t('status_now_in_progress'),
                confirmButtonColor: '#10b981'
            });
            onSuccess();
        } catch (err) {
            console.error(err);

            // 🛑 Affiche l'erreur retournée par le backend
            const errorMessage =
                err.response?.data?.error || // message envoyé par le backend
                t('error_starting_reservation'); // fallback

            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: errorMessage,
                confirmButtonColor: '#3b82f6'
            });
        }
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-6 rounded-xl shadow-2xl max-w-md w-full mx-4">
                    <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                    <p className="text-center mt-4 text-gray-700">{t('loading_details')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden">
                {/* Header */}
                <div className="bg-blue-600 px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-white">{t('start_reservation_title')}</h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-blue-200 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Client & Vehicle Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Client Card */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className={`flex items-center mb-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <User className={`w-5 h-5 text-blue-600 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                <h3 className="font-medium text-gray-800">{t('client_info')}</h3>
                            </div>
                            <div className="space-y-2 text-sm">
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('name')}:</span>
                                    <span className={isRtl ? 'mr-0' : ''}>{details.client_name_full || `${t('client')} #${details.client_id}`}</span>
                                </p>
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('phone')}:</span>
                                    <span>{details.client_phone || 'N/A'}</span>
                                </p>
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('reservation_id')}:</span>
                                    <span className="font-mono">{details.client_id}</span>
                                </p>
                            </div>
                        </div>

                        {/* Vehicle Card */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className={`flex items-center mb-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <Car className={`w-5 h-5 text-blue-600 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                <h3 className="font-medium text-gray-800">{t('vehicle_info')}</h3>
                            </div>
                            <div className="space-y-2 text-sm">
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('model')}:</span>
                                    <span>{details.voiture_marque} {details.voiture_modele}</span>
                                </p>
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('license_plate')}:</span>
                                    <span className="font-mono">{details.voiture_immatriculation}</span>
                                </p>
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('vehicle')} ID:</span>
                                    <span className="font-mono">{details.voiture_id}</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Reservation Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Location Info */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className={`flex items-center mb-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <MapPin className={`w-5 h-5 text-blue-600 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                <h3 className="font-medium text-gray-800">{t('locations')}</h3>
                            </div>
                            <div className="space-y-2 text-sm">
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('departure')}:</span>
                                    <span>{details.lieu_depart}</span>
                                </p>
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('return')}:</span>
                                    <span>{details.lieu_retour}</span>
                                </p>
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('km_departure_label')}:</span>
                                    <span>{details.km_depart} km</span>
                                </p>
                            </div>
                        </div>

                        {/* Dates & Pricing */}
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className={`flex items-center mb-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <Calendar className={`w-5 h-5 text-blue-600 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                <h3 className="font-medium text-gray-800">{t('dates_tarifs')}</h3>
                            </div>
                            <div className="space-y-2 text-sm">
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('departure')}:</span>
                                    <span>{moment(details.date_depart).format('DD/MM/YYYY HH:mm')}</span>
                                </p>
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('return_date_time')}:</span>
                                    <span>{moment(details.date_retour_prevue).format('DD/MM/YYYY HH:mm')}</span>
                                </p>
                                <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('total')}:</span>
                                    <span className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <DollarSign className={`w-4 h-4 ${isRtl ? 'ml-1' : 'mr-1'}`} />
                                        {Number(details.total || 0).toFixed(2)} {t('currency')}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Status & Agent */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
                        <div className={`flex items-center mb-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <Clock className={`w-5 h-5 text-blue-600 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                            <h3 className="font-medium text-gray-800">{t('status')}</h3>
                        </div>
                        <div className={`grid grid-cols-2 gap-4 text-sm ${isRtl ? 'text-right' : ''}`}>
                            <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('status')}:</span>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${details.status === 'en_attente' ? 'bg-yellow-100 text-yellow-800' :
                                        details.status === 'en_cours' ? 'bg-blue-100 text-blue-800' :
                                            'bg-green-100 text-green-800'
                                    }`}>
                                    {t(details.status)}
                                </span>
                            </p>
                            <p className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <span className={`font-medium text-gray-700 w-28 ${isRtl ? 'text-right' : 'text-left'}`}>{t('agent')}:</span>
                                <span>{details.agent_reservation || 'N/A'}</span>
                            </p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className={`flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <button
                            onClick={handleStart}
                            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white transition-colors ${isToday(details.date_depart)
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-gray-400 cursor-not-allowed'
                                }`}
                        >
                            <span>{t('start_reservation_btn')}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
